import React from 'react';
import { Typography, Select, MenuItem, Box, TextField } from '@mui/material';
import { SetOnHoldButton } from './SetOnHoldButton';

enum AnswerType {
  input = 0,
  bool = 1,
  list = 2,
  int = 3,
  float = 4,
  text = 5
}

interface TokenInputProps {
  token: string;
  questionId: string;
  tokenValues: Record<string, Record<string, any>>;
  placeholders: any[];
  companies: any[];
  onTokenValueChange: (questionId: string, token: string, value: any) => void;
  onPlacedOnHoldCompleted: (comment: string) => Promise<void>;
  isReadOnly?: boolean;
  onBlur?: (questionId: string, token: string, value: any) => void;
}

const TokenInput: React.FC<TokenInputProps> = ({
  token,
  questionId,
  tokenValues,
  placeholders,
  companies,
  onTokenValueChange,
  onPlacedOnHoldCompleted,
  isReadOnly,
  onBlur
}) => {
  const placeholder = placeholders.find((item) => item.Key === token);

  const questionTokens = tokenValues[questionId] || {};
  const tokenValue = questionTokens[token] || '';

  const handleBlur = () => {
    if (onBlur) {
      onBlur(questionId, token, tokenValue);
    }
  };

  const handlePlacedOnHoldCompleted = async (comment: string) => {
    onPlacedOnHoldCompleted?.(comment);
  };

  if (!placeholder) {
    if (token === 'waterSupplierCompanyName') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
          <Typography variant="body2" sx={{ width: '250px', flexShrink: 0 }}>
            Water Supplier
          </Typography>
          <Select
            value={tokenValue}
            onChange={(e) => onTokenValueChange(questionId, token, e.target.value)}
            onBlur={handleBlur}
            size="small"
            disabled={isReadOnly}
            sx={{ width: '250px', bgcolor: 'grey.50' }}
          >
            {[...companies]
              .sort((a, b) => a.Id.localeCompare(b.Id))
              .map((company) => (
                <MenuItem key={company.Id} value={company.Id}>
                  {company.Id}
                </MenuItem>
              ))}
          </Select>
          <SetOnHoldButton waterCompanyName={tokenValue} onPlacedOnHoldCompleted={handlePlacedOnHoldCompleted}></SetOnHoldButton>
        </Box>
      );
    }

    if (token === 'waterSupplierAddress') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
          <Typography variant="body2" sx={{ width: '250px', flexShrink: 0 }}>
            Water Supplier Address
          </Typography>
          <TextField
            value={tokenValue || ''}
            size="small"
            disabled
            sx={{
              width: '250px',
              bgcolor: 'grey.50',
              '& .MuiInputBase-input': {
                color: 'text.primary'
              }
            }}
            InputProps={{
              placeholder: !tokenValue ? 'Auto-filled from supplier selection' : undefined
            }}
          />
        </Box>
      );
    }

    return null;
  }

  const { Type, Label, Options, Nullable } = placeholder;

  const renderInputField = () => {
    switch (Type) {
      case AnswerType.input:
      case AnswerType.text:
        return (
          <TextField
            value={tokenValue}
            onChange={(e) => onTokenValueChange(questionId, token, e.target.value)}
            onBlur={handleBlur}
            required={!Nullable}
            disabled={isReadOnly}
            size="small"
            placeholder="Enter Text"
            sx={{ width: '250px', bgcolor: 'grey.50' }}
          />
        );
      case AnswerType.bool:
        return (
          <Select
            value={tokenValue}
            onChange={(e) => onTokenValueChange(questionId, token, e.target.value)}
            onBlur={handleBlur}
            required={!Nullable}
            disabled={isReadOnly}
            size="small"
            sx={{ width: '250px', bgcolor: 'grey.50' }}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        );
      case AnswerType.list:
        return (
          <Select
            value={tokenValue}
            onChange={(e) => onTokenValueChange(questionId, token, e.target.value)}
            onBlur={handleBlur}
            required={!Nullable}
            disabled={isReadOnly}
            size="small"
            sx={{ width: '250px', bgcolor: 'grey.50' }}
          >
            {Options?.map((option: string) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      case AnswerType.int:
      case AnswerType.float:
        return (
          <TextField
            type="number"
            inputProps={{ step: Type === AnswerType.float ? 0.01 : 1 }}
            value={tokenValue}
            onChange={(e) =>
              onTokenValueChange(questionId, token, Type === AnswerType.float ? parseFloat(e.target.value) : parseInt(e.target.value, 10))
            }
            onBlur={handleBlur}
            required={!Nullable}
            disabled={isReadOnly}
            size="small"
            placeholder="Enter Number"
            sx={{ width: '250px', bgcolor: 'grey.50' }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
      <Typography variant="body2" sx={{ width: '250px', flexShrink: 0 }}>
        {Label}
      </Typography>
      {renderInputField()}
    </Box>
  );
};

export default TokenInput;
